import React from 'react';
import { useSelector } from 'react-redux';

import { getMarket } from '@travel/i18n';
import { getL10nNumber } from '@travel/i18n/src/components/L10nNumber';
import { Coupon, Global, ToriGate } from '@travel/icons/ui';
import { cx } from '@travel/utils';

import Money from 'components/Money';

import { PROVIDER_CONTRACT_TYPES, type ProviderContractType } from 'constants/provider';
import { INCENTIVE_CALCULATION_METHOD, INCENTIVE_METHOD } from 'constants/salesPromotion';
import { Translate } from 'core/translate';
import type { IncentiveAmount } from 'SalesPromotion-Types';

import styles from './incentiveValueTitle.module.scss';

type Props = Partial<IncentiveAmount> & {
  currency: string;
  localCurrency?: string;
  isShowCouponIcon?: boolean;
  className?: string;
  localAmountClassName?: string;
  contractType?: ProviderContractType;
  isExpired?: boolean;
};

export const IncentiveValueTitle = (props: Props) => {
  const marketFromStore = useSelector(getMarket)?.marketCode || '';

  const isDiscountPromotion = props?.incentiveMethod === INCENTIVE_METHOD.DISCOUNT;
  const isPercentValue = props?.incentiveCalcMethod === INCENTIVE_CALCULATION_METHOD.PERCENT;
  const isIndirectContract = props?.contractType === PROVIDER_CONTRACT_TYPES.INDIRECT;
  const status = isDiscountPromotion ? 'discount_promotion' : 'point_promotion';

  const isShowLocalAmount =
    !isPercentValue &&
    ((isDiscountPromotion && props.localCurrency && props.currency !== props.localCurrency) ||
      (!isDiscountPromotion && props.pointCurrency && props.currency !== props.pointCurrency));

  const percentage = props?.percentage
    ? getL10nNumber(marketFromStore, props?.percentage || 0, undefined, true)
    : 0;

  return (
    <div className={props.className} data-testid="incentiveValueTitle-wrapper">
      <div
        data-testid="incentiveValueTitle-userAmount"
        className={styles.incentiveValueTitleContainer}
      >
        <div>
          {props.isShowCouponIcon && <Coupon size={20} />}
          <span className={styles.incentiveValueTitle}>
            <Translate
              id="Coupon_Details.Coupon.Value_And_Suffix"
              condition={() => `status == ${status}`}
              data={{
                percentage: isPercentValue ? (
                  <span>{`${percentage}%`}</span>
                ) : (
                  <Money value={props?.amount || 0} />
                ),
                point_back: isPercentValue ? (
                  <span>{`${percentage}%`}</span>
                ) : (
                  <Money value={props?.amount || 0} currency={props?.pointCurrency} />
                ),
              }}
            />
            {isShowLocalAmount && <span>&nbsp;*</span>}
          </span>
        </div>
        {props.contractType && (
          <span className={styles.contractTypeRegionContainer}>
            {isIndirectContract ? (
              <Global size={16} className={props.isExpired ? styles.disabledIcon : undefined} />
            ) : (
              <ToriGate size={16} className={props.isExpired ? styles.disabledIcon : undefined} />
            )}
            <span className={cx(styles.contractTypeRegion, props.isExpired && styles.disabledText)}>
              <Translate
                id="Coupon_Details_Widget.Coupon.Region.Icon"
                condition={() => (isIndirectContract ? 'status == Indirect' : 'status == Direct')}
                className={styles.contractTypeRegion}
              />
            </span>
          </span>
        )}
      </div>
      {isShowLocalAmount && (
        <div data-testid="incentiveValueTitle-localAmount" className={props.localAmountClassName}>
          {isDiscountPromotion ? (
            <Translate
              id="Coupon_Details.Maximum_Discount.Local_Amount.Displayed_Information"
              condition={() => `status == different_currency`}
              data={{
                local_amount: (
                  <Money value={props?.localAmount || 0} currency={props?.localCurrency} />
                ),
              }}
            />
          ) : (
            <Translate
              id="Coupon_List.Coupon.Local_Amount"
              condition={() => `status == different_currency`}
              data={{
                local_amount: (
                  <Translate
                    id="Coupon_Details.Coupon.Value_And_Suffix"
                    condition={() => `status == ${status}`}
                    data={{
                      point_back: (
                        <Money value={props?.localAmount || 0} currency={props.localCurrency} />
                      ),
                    }}
                  />
                ),
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default IncentiveValueTitle;
